import React, { useEffect, useState } from 'react';
import { Box, Form, Heading, useAnalytics } from '@medely/ui-kit';
import { WorkExperienceForm } from './WorkExperienceForm';
import { useCurrentUser } from '../../../../../hooks';
import { useWorkExperienceMutations } from '../mutations';
import { ApplicationButtons } from '../../../components/ApplicationButtons';
import { createMostRecentWorkExperienceValidation } from './schema';
import { useWorkExperience } from '../hooks/useWorkExperience';
import { SetPageProps, FormValuesProps } from '../interface';
import { useApplicationMutations } from '@mp/pages/application/applicationFlowMutations';
import { FixedFooterLayout } from '@mp/components';
import { FragmentType, graphql, parseFragmentData } from '@mp/graphql/generated';

const WorkExperiencePrimaryFormCurrentUserGql = graphql(`
  fragment WorkExperiencePrimaryFormCurrentUser on Account {
    id
    ...WorkExperienceFormCurrentUser
    ...UseWorkExperienceCurrentUser
  }
`);

type WorkExperiencePrimaryFormProps = {
  setPage: (value: SetPageProps) => void;
  currentUser?: FragmentType<typeof WorkExperiencePrimaryFormCurrentUserGql>;
  hasOnlyOnePosition: boolean;
  isMobileVersion: boolean;
};

export const WorkExperiencePrimaryForm = ({
  setPage,
  currentUser: inputCurrentUser,
  hasOnlyOnePosition,
  isMobileVersion,
}: WorkExperiencePrimaryFormProps) => {
  const { proType } = useCurrentUser();
  const currentUser = parseFragmentData(WorkExperiencePrimaryFormCurrentUserGql, inputCurrentUser);
  const analytics = useAnalytics();
  const { totalYearsOfExperience, positionsOptions, createPayload, isMissingYoE } =
    useWorkExperience({ currentUser: currentUser || undefined });
  const { submitWorkExperienceInfo } = useWorkExperienceMutations();
  const { prevStep, isLoading } = useApplicationMutations();

  const [canContinue, setCanContinue] = useState(true);

  const defaultValues = hasOnlyOnePosition ? { position_id: positionsOptions[0].value } : {};

  const handleSubmit = (values: FormValuesProps) => {
    const parsedValues = createPayload(values);
    submitWorkExperienceInfo(parsedValues, {
      onSuccess: () => {
        if (
          isMissingYoE({
            startDate: values.start_date.toDateString(),
            endDate: !!values.end_date ? values.end_date.toDateString() : undefined,
            positionId: values.position_id,
          })
        ) {
          setPage('addMoreMonths');
        } else {
          setPage('experienceSummary');
        }
      },
    });
  };

  useEffect(() => {
    analytics.track('Product Application Primary Experience - Viewed', {
      account_id: currentUser?.id,
      context: 'Product Application Primary Experience',
      subcontext: null,
      action: 'Viewed',
      pro_type: proType,
    });
  }, []);

  return (
    <Form
      handleSubmit={handleSubmit}
      schema={createMostRecentWorkExperienceValidation(totalYearsOfExperience)}
      defaultValues={defaultValues}
    >
      <FixedFooterLayout
        footerContent={
          <ApplicationButtons disabled={isLoading || !canContinue} backClick={prevStep} />
        }
        height="97%"
      >
        <Box pb={2}>
          <Heading
            size="s"
            color="text.primary"
            testId="work-experience-primary-form-title"
            textAlign={isMobileVersion ? 'center' : 'left'}
          >
            Add detail for your most recent paid clinical experience in the U.S.
          </Heading>
        </Box>
        <WorkExperienceForm
          currentUser={currentUser}
          setPage={setPage}
          showWageLog={true}
          onValidationChange={setCanContinue}
        />
      </FixedFooterLayout>
    </Form>
  );
};
